import { arcadeApiClient } from '../apiClients'
import { AppContext } from '../providers'
import { v4 as randomUUID } from 'uuid'
import { useCallback, useContext, useEffect, useRef, useState } from 'react'
import useSWR from 'swr'
import { NewsfeedItem } from 'types/NewsfeedItem'

const fetcher = (query): Promise<NewsfeedItem[]> => {
  return arcadeApiClient
    .get(`/multi_platform/newsfeed/${query}`)
    .then(res => res.data.newsfeedItems)
}

export const useNewsfeedItems = ({
  feedType,
  teamId,
  userId,
  campaignId,
  enabled = true,
  limit = 5,
}: {
  feedType: string
  teamId?: number
  userId?: number
  campaignId?: number
  enabled?: boolean
  limit?: number
}) => {
  const { setToast } = useContext(AppContext)
  const [lastId, setLastId] = useState<number>()
  const [more, setMore] = useState(true)
  const [newsfeedItems, setNewsfeedItems] = useState<NewsfeedItem[]>([])
  // Fingerprint is a random number we append to the request URL to avoid using swr cached data
  const [fingerPrint, setFingerPrint] = useState<number>(randomUUID())
  // When true, the next page of data will replace items instead of appending
  const replaceOnNextDataRef = useRef(false)
  useEffect(() => {
    setNewsfeedItems([])
    setMore(true)
    setLastId(undefined)
    setFingerPrint(randomUUID())
  }, [feedType, teamId, userId, campaignId, enabled])

  const query = new URLSearchParams()
  query.append('limit', limit.toString())
  teamId && query.append('team_id', teamId.toString())
  lastId && query.append('last_id', lastId.toString())
  userId && query.append('assumed_user_id', userId.toString())
  campaignId && query.append('campaign_id', campaignId.toString())
  const { data: newsfeedPage, isValidating: loading, error } = useSWR(
    enabled ? `${feedType}?${query.toString()}#${fingerPrint}` : null,
    fetcher,
    { revalidateOnMount: true },
  )

  useEffect(() => {
    if (!newsfeedPage) return
    if (replaceOnNextDataRef.current) {
      setNewsfeedItems(newsfeedPage)
      replaceOnNextDataRef.current = false
    } else {
      setNewsfeedItems(items => {
        const idSet = new Set(items.map(item => item.id))
        const newItems = newsfeedPage.filter(item => !idSet.has(item.id))
        return [...items, ...newItems]
      })
    }
  }, [newsfeedPage])

  const loadMore = useCallback(() => {
    if (!enabled) return
    if (!newsfeedPage) return
    setLastId(newsfeedItems[newsfeedItems.length - 1]?.id)
    if (newsfeedPage.length < limit) {
      setMore(false)
    }
  }, [enabled, setLastId, newsfeedItems, newsfeedPage, limit])

  const pollingQuery = new URLSearchParams()
  pollingQuery.append('limit', limit.toString())
  teamId && pollingQuery.append('team_id', teamId.toString())
  userId && pollingQuery.append('assumed_user_id', userId.toString())
  campaignId && pollingQuery.append('campaign_id', campaignId.toString())
  const { data: firstPage } = useSWR(
    // Poll is appended to the end of this URL to distiguish it from the initial newsfeed request URL
    enabled ? `${feedType}?${pollingQuery.toString()}#${fingerPrint}-poll` : null,
    fetcher,
    {
      refreshInterval: 30000,
      isPaused: () => !enabled || loading || !newsfeedPage,
    },
  )

  useEffect(() => {
    if (!firstPage) return
    setNewsfeedItems(items => {
      const ids = items.map(item => item.id)
      const newItems = firstPage.filter(item => !ids.includes(item.id))
      return [...newItems, ...items]
    })
  }, [firstPage])

  const refresh = useCallback(() => {
    replaceOnNextDataRef.current = true
    setMore(true)
    setLastId(undefined)
    setFingerPrint(randomUUID())
  }, [])

  const deletePost = (id: number) =>
    arcadeApiClient
      .delete(`multi_platform/newsfeed/${id}`)
      .then(() => {
        setNewsfeedItems(newsfeedItems.filter(item => item.id !== id))
      })
      .catch(e => {
        setToast({
          status: 'error',
          description: `Could not Delete Post: ${e}`,
        })
        console.error(e)
      })

  return {
    deletePost,
    error,
    hasMore: enabled ? more : false,
    loading,
    loadMore,
    newsfeedItems,
    refresh,
  }
}
